import Statement, { Description } from '@/components/Statement';
import { useCompanyCanonicalUrl } from '@/hooks/useCompany';
import useCompanyStatementsAPIFormatted from '@/hooks/useCompanyStatementsAPIFormatted';
import ChartWrapper from '@/pages/CompanyReport/components/Overview/components/ChartWrapper';
import { useValuationData } from '@/pages/CompanyReport/components/Valuation';
import { formatterFactor } from '@/utilities/format';
import loadable from '@loadable/component';
import { Button, Grid } from '@simplywallst/ui-core';
import {
  ValuationTranslation,
  useValuationTranslation,
} from '@/pages/CompanyReport/components/Valuation/hooks/useValuationTranslation';
import FairRatioTable from './components/FairRatioTable';
import { SWS_HOST } from '@/constants';

const GaugeFairValue = loadable(() => import('@/components/GaugeFairValue'));

const HelpContent = () => (
  <div>
    <ValuationTranslation i18nKey="vsFair.help.description">
      <p>
        Every company is unique, and professionals understand that using a rule
        of thumb or average ratio is only one part of valuing a company. The
        Simply Wall St Fair Ratio uses thousands of data points to understand
        how much the market pays for growth and risk, and combines that with
        industry data to build the intuition that a professional with decades of
        experience might have.
      </p>
      <p>
        For a more detailed breakdown of how compare stocks to their industry,
        please check out our
        <Button
          as="a"
          target="_blank"
          href={`${SWS_HOST}/help/articles/hc/en-us/articles/4751563581071-Understanding-the-Valuation-section-in-the-company-report#Simply-Wall-St-Fair-Ratio`}
          rel="noopener noreferrer"
          styleType="strongLink"
        >
          Help Centre.
        </Button>
      </p>
    </ValuationTranslation>
  </div>
);

export const FairRatio = () => {
  const {
    multiples: { preferredMultiple },
    preferredMultipleFairRatio,
    formattedPreferredMultipleFairRatio,
  } = useValuationData();

  const canonicalUrl = useCompanyCanonicalUrl();
  const { data: statements } = useCompanyStatementsAPIFormatted({
    canonicalURL: canonicalUrl,
  });

  const noFairRatioData = formattedPreferredMultipleFairRatio === 'n/a';

  const t = useValuationTranslation();

  return (
    <Grid templateColumnsMd="50% auto" gap="24px">
      <Grid alignContent="center">
        <ChartWrapper
          id="fair-ratio"
          chart={
            noFairRatioData ? (
              <GaugeFairValue
                value={null}
                valueLabel={{ name: '', value: '' }}
                fairValue={62}
                fairValueLabel={{
                  name: t('vsFair.chart.gauge.label', {
                    metric: preferredMultiple.name,
                  }),
                  value: t('vsFair.chart.gauge.noValue'),
                }}
                flip={true}
                formatter={formatterFactor}
              />
            ) : (
              <GaugeFairValue
                value={preferredMultiple.valueRaw}
                valueLabel={{
                  name: t('vsFair.chart.gauge.valueLabel', {
                    metric: preferredMultiple.name,
                  }),
                  value: preferredMultiple.value,
                }}
                fairValue={preferredMultipleFairRatio}
                fairValueLabel={{
                  name: t('vsFair.chart.gauge.label', {
                    metric: preferredMultiple.name,
                  }),
                  value: formattedPreferredMultipleFairRatio,
                }}
                flip={true}
                formatter={formatterFactor}
              />
            )
          }
          help={<HelpContent />}
          helpTitle={t('vsFair.help.title')}
          feedbackLabel={t('vsFair.chart.feedback')}
          table={<FairRatioTable />}
          preRenderHeight={
            (preferredMultiple.valueRaw < 0 ||
              preferredMultiple.valueRaw > 2 * preferredMultipleFairRatio) &&
            preferredMultipleFairRatio > 0
              ? 270
              : 200
          }
        ></ChartWrapper>
      </Grid>
      <Grid alignContent="center">
        <Statement
          statementId="below-fair-value"
          title={statements?.IsGoodValueComparingRatioToFairRatio?.title}
          description={
            <Description
              statement={statements?.IsGoodValueComparingRatioToFairRatio}
            />
          }
          value={statements?.IsGoodValueComparingRatioToFairRatio?.value}
        />
      </Grid>
    </Grid>
  );
};
